/**
 * i18n Configuration
 * react-i18nextの設定
 *
 * 【言語検出の優先順位】
 * 1. URLクエリパラメータ (?lng=ja) - 開発者向けデバッグ・テスト用
 * 2. localStorage - ユーザーが手動で選択した言語設定
 * 3. navigator.language - ブラウザの言語設定
 * 4. デフォルト (en) - フォールバック
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";

// 翻訳リソース
import en from "../locales/en.json";
import ja from "../locales/ja.json";

// サポートされている言語コード（型安全性のため）
export type SupportedLanguage = "en" | "ja";

// サポートされている言語コードのリスト（将来的な拡張を考慮）
const SUPPORTED_LANGUAGES: SupportedLanguage[] = ["en", "ja"];

// デフォルト言語
const DEFAULT_LANGUAGE: SupportedLanguage = "en";

// localStorage キー
const STORAGE_KEY = "ai-deck-language";

/**
 * URLクエリパラメータから言語設定を取得
 * 例: ?lng=ja または ?lng=en
 * @returns 有効な言語コードまたはnull
 */
const getLanguageFromQuery = (): SupportedLanguage | null => {
  if (typeof window === "undefined") return null;

  const params = new URLSearchParams(window.location.search);
  const lng = params.get("lng");

  if (lng && SUPPORTED_LANGUAGES.includes(lng as SupportedLanguage)) {
    return lng as SupportedLanguage;
  }

  return null;
};

/**
 * ブラウザの言語設定を取得
 * navigator.languageから言語コードを抽出し、サポートされている言語にマッピング
 *
 * 【対応言語コード例】
 * - ja, ja-JP → ja (日本語)
 * - en, en-US, en-GB → en (英語)
 * - zh, zh-CN, zh-TW → en (中国語は現在未対応のため英語にフォールバック)
 * - ko, ko-KR → en (韓国語は現在未対応のため英語にフォールバック)
 *
 * @returns サポートされている言語コード
 */
export const getBrowserLanguage = (): SupportedLanguage => {
  if (typeof navigator === "undefined") return DEFAULT_LANGUAGE;

  // navigator.languageから言語コード部分を抽出（'en-US' -> 'en'）
  const browserLang = navigator.language.split("-")[0].toLowerCase();

  // 日本語の検出
  if (browserLang === "ja") {
    return "ja";
  }

  // 将来的なサポート予定言語（現在は英語にフォールバック）
  // if (browserLang === "zh") return "zh"; // 中国語（簡体字）
  // if (browserLang === "ko") return "ko"; // 韓国語
  // if (browserLang === "fr") return "fr"; // フランス語
  // if (browserLang === "de") return "de"; // ドイツ語
  // if (browserLang === "es") return "es"; // スペイン語

  // サポートされていない言語は英語にフォールバック
  return DEFAULT_LANGUAGE;
};

/**
 * ローカルストレージから保存された言語設定を取得
 * @returns 保存された有効な言語コードまたはnull
 */
const getSavedLanguage = (): SupportedLanguage | null => {
  if (typeof localStorage === "undefined") return null;

  const saved = localStorage.getItem(STORAGE_KEY);

  if (saved && SUPPORTED_LANGUAGES.includes(saved as SupportedLanguage)) {
    return saved as SupportedLanguage;
  }

  return null;
};

/**
 * 初期言語設定を決定
 * 優先順位: URLクエリ > localStorage > ブラウザ設定 > デフォルト
 */
const initialLanguage = getLanguageFromQuery() || getSavedLanguage() || getBrowserLanguage();

i18n
  .use(initReactI18next) // react-i18nextを使用
  .init({
    resources: {
      en: { translation: en },
      ja: { translation: ja },
    },
    lng: initialLanguage, // 初期言語
    fallbackLng: "en", // フォールバック言語
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    react: {
      // Suspense無効化: アプリ起動時のちらつきを防ぐため
      // React 19でも互換性があり、現在の実装で問題なく動作
      useSuspense: false,
      // 言語変更時にコンポーネントを自動再レンダリング
      // 'languageChanged'イベントをリスニングして、翻訳キーを使用する全コンポーネントを更新
      bindI18n: "languageChanged",
    },
  });

/**
 * <html lang> を実際の表示言語に合わせる。
 *
 * index.html は lang="ja" 固定なので、英語表示のまま放置するとスクリーンリーダーが
 * 英文を日本語の音で読み上げ、検索エンジンにも誤った言語を伝える。
 * SPA なのでここ 1 箇所で全ページに効く。
 */
const syncHtmlLang = (lng: string): void => {
  if (typeof document === "undefined") return;
  document.documentElement.lang = lng;
};

// 初回ペイント前に初期言語を反映（viewSettings の FOUC 対策と同じ方針）
syncHtmlLang(initialLanguage);

// 言語変更時にローカルストレージに保存
i18n.on("languageChanged", (lng) => {
  syncHtmlLang(lng);
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(STORAGE_KEY, lng);
  }
});

// 開発環境のみ: windowオブジェクトにi18nを公開（デバッグ用）
declare global {
  interface Window {
    i18n?: typeof i18n;
  }
}

if (typeof window !== "undefined" && import.meta.env.DEV) {
  window.i18n = i18n;
}

export default i18n;
